import ExcelJS from 'exceljs';
import { getUnits } from '../services/unitsService';
import { getWarehouses } from '../services/warehousesService';
import { productService } from '../services/productService';
import {
  cellText,
  parseActiveCell,
  summarizeImportResult,
} from './classificationExcel';

export { summarizeImportResult };

export const PRODUCT_BULK_COL = {
  id: 'ID',
  sku: 'SKU (referencia)',
  name: 'Nombre',
  unit: 'Unidad de medida',
  minStock: 'Stock mínimo',
  active: 'Activo (Sí/No)',
  isInsumo: 'Tratar como insumo (Sí/No)',
  isVendible: 'Producto vendible (Sí/No)',
  machineryReading: 'Registrar lectura vehículo/máquina (Sí/No)',
  warehouses: 'Almacenes visibles (códigos ; separados)',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

const UNITS_SHEET_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE0F2FE' },
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels, fill = HEADER_FILL) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = fill;
  });
}

function boolLabel(value) {
  return value ? 'Sí' : 'No';
}

function warehouseCodes(product) {
  const list = Array.isArray(product?.warehouses) ? product.warehouses : [];
  return list
    .map((w) => w.code)
    .filter(Boolean)
    .join('; ');
}

export function buildBulkEditHeaders(opts = {}) {
  const headers = [
    PRODUCT_BULK_COL.id,
    PRODUCT_BULK_COL.sku,
    PRODUCT_BULK_COL.name,
    PRODUCT_BULK_COL.unit,
    PRODUCT_BULK_COL.minStock,
    PRODUCT_BULK_COL.active,
  ];
  if (opts.insumoEnabled) headers.push(PRODUCT_BULK_COL.isInsumo);
  if (opts.ventasEnabled) headers.push(PRODUCT_BULK_COL.isVendible);
  if (opts.machineryEnabled) headers.push(PRODUCT_BULK_COL.machineryReading);
  if (opts.warehouseVisibilityEnabled) headers.push(PRODUCT_BULK_COL.warehouses);
  return headers;
}

function productToRow(product, headers, opts) {
  const row = {
    [PRODUCT_BULK_COL.id]: product.id,
    [PRODUCT_BULK_COL.sku]: product.sku || '',
    [PRODUCT_BULK_COL.name]: product.name || '',
    [PRODUCT_BULK_COL.unit]: product.unit_of_measure || '',
    [PRODUCT_BULK_COL.minStock]: product.min_stock ?? 0,
    [PRODUCT_BULK_COL.active]: boolLabel(product.is_active !== false),
  };
  if (opts.insumoEnabled) {
    row[PRODUCT_BULK_COL.isInsumo] = boolLabel(Boolean(product.is_insumo));
  }
  if (opts.ventasEnabled) {
    row[PRODUCT_BULK_COL.isVendible] = boolLabel(Boolean(product.is_vendible));
  }
  if (opts.machineryEnabled) {
    row[PRODUCT_BULK_COL.machineryReading] = boolLabel(Boolean(product.machinery_register_enabled));
  }
  if (opts.warehouseVisibilityEnabled) {
    row[PRODUCT_BULK_COL.warehouses] = warehouseCodes(product);
  }
  return headers.map((h) => row[h] ?? '');
}

export async function downloadProductsBulkEditSheet(products = [], opts = {}) {
  const headers = buildBulkEditHeaders(opts);
  const wb = new ExcelJS.Workbook();

  const ws = wb.addWorksheet('Productos', { views: [{ state: 'frozen', ySplit: 3 }] });
  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);

  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value =
    'Edición masiva de productos. No modifique ID ni SKU. La unidad debe ser un código del catálogo (hoja Unidades). Edite solo las filas que necesite cambiar y vuelva a importar.';
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 48;

  ws.mergeCells(`A2:${lastCol}2`);
  ws.getCell('A2').value = `${products.length} producto(s) · Super administrador`;
  ws.getCell('A2').font = { italic: true, color: { argb: 'FF4B5563' } };

  styleHeaderRow(ws.getRow(3), headers);

  const sorted = [...products].sort((a, b) =>
    String(a?.sku || '').localeCompare(String(b?.sku || ''), 'es', { sensitivity: 'base' }),
  );
  sorted.forEach((product) => {
    ws.addRow(productToRow(product, headers, opts));
  });

  ws.columns = headers.map((h, i) => {
    if (h === PRODUCT_BULK_COL.name) return { width: 36 };
    if (h === PRODUCT_BULK_COL.sku) return { width: 22 };
    if (i === 0) return { width: 8 };
    return { width: 18 };
  });

  const unitsRes = await getUnits({ active_only: true });
  const units = unitsRes?.success && Array.isArray(unitsRes.data) ? unitsRes.data : [];
  const unitsWs = wb.addWorksheet('Unidades');
  styleHeaderRow(unitsWs.getRow(1), ['Código', 'Nombre', 'Símbolo'], UNITS_SHEET_FILL);
  units.forEach((u) => {
    unitsWs.addRow([u.code || '', u.name || '', u.symbol || '']);
  });
  unitsWs.columns = [{ width: 12 }, { width: 28 }, { width: 12 }];

  const buf = await wb.xlsx.writeBuffer();
  const date = new Date().toISOString().split('T')[0];
  downloadBuffer(buf, `edicion_masiva_productos_${date}.xlsx`);
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet, requiredLabels) {
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const row = sheet.getRow(r);
    const map = headerMapFromRow(row);
    if (requiredLabels.every((label) => map[label] != null)) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

function parseMinStock(raw) {
  if (raw == null || raw === '') return { ok: true, value: null, skip: true };
  const n = parseInt(String(raw).trim(), 10);
  if (Number.isNaN(n) || n < 0) return { ok: false, value: 0, skip: false };
  return { ok: true, value: n, skip: false };
}

function resolveUnitCode(raw, unitByCode) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { ok: true, code: null, skip: true };
  const key = trimmed.toLowerCase();
  const unit = unitByCode.get(key);
  if (!unit) {
    return {
      ok: false,
      error: `Unidad «${trimmed}» no está en el catálogo activo. Use un código de la hoja Unidades.`,
    };
  }
  return { ok: true, code: unit.code, skip: false };
}

function resolveWarehouseIds(raw, warehouseByCode) {
  if (raw == null || String(raw).trim() === '') {
    return { ok: true, ids: null, skip: true, clear: false };
  }
  const trimmed = String(raw).trim();
  if (trimmed === '-' || trimmed.toLowerCase() === 'todos') {
    return { ok: true, ids: [], skip: false, clear: true };
  }
  const parts = trimmed.split(/[;,]/).map((p) => p.trim()).filter(Boolean);
  const ids = [];
  for (const code of parts) {
    const wh = warehouseByCode.get(code.toLowerCase());
    if (!wh) {
      return { ok: false, error: `Almacén no encontrado: «${code}».` };
    }
    ids.push(wh.id);
  }
  return { ok: true, ids: [...new Set(ids)], skip: false, clear: false };
}

function formatApiError(err) {
  const d = err.response?.data;
  if (!d) return err.message || 'Error desconocido';
  if (d.message) return d.message;
  if (d.errors) return Object.values(d.errors).flat().join('; ');
  return 'Error al guardar';
}

const REQUIRED_HEADERS = [PRODUCT_BULK_COL.id, PRODUCT_BULK_COL.unit];

export async function analyzeProductsBulkEdit(file, opts = {}) {
  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findHeaderRow(sheet, REQUIRED_HEADERS);
  if (!found) {
    return {
      success: false,
      fatalMessage: `Faltan encabezados obligatorios. Debe existir al menos: ${REQUIRED_HEADERS.join(', ')}.`,
    };
  }

  const [unitsRes, warehousesRes] = await Promise.all([
    getUnits({ active_only: true }),
    opts.warehouseVisibilityEnabled ? getWarehouses({ active_only: true }) : Promise.resolve({ success: true, data: [] }),
  ]);

  if (!unitsRes?.success || !Array.isArray(unitsRes.data)) {
    return { success: false, fatalMessage: 'No se pudo cargar el catálogo de unidades.' };
  }

  const unitByCode = new Map(
    unitsRes.data
      .filter((u) => u.code)
      .map((u) => [String(u.code).toLowerCase(), u]),
  );

  const warehouseByCode = new Map(
    (warehousesRes?.data || [])
      .filter((w) => w.code)
      .map((w) => [String(w.code).toLowerCase(), w]),
  );

  const productsRes = await productService.getProducts();
  if (!productsRes?.success || !Array.isArray(productsRes.data)) {
    return { success: false, fatalMessage: 'No se pudieron cargar los productos del sistema.' };
  }

  const productById = new Map(productsRes.data.map((p) => [Number(p.id), p]));

  const { rowIndex: headerRow, colMap } = found;
  const last = getLastUsedRow(sheet);
  const previewRows = [];
  const validTasks = [];

  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const idRaw = cellText(row.getCell(colMap[PRODUCT_BULK_COL.id]));
    const skuRaw = colMap[PRODUCT_BULK_COL.sku] ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.sku])) : '';
    const nameRaw = colMap[PRODUCT_BULK_COL.name] ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.name])) : '';
    const unitRaw = cellText(row.getCell(colMap[PRODUCT_BULK_COL.unit]));
    const minRaw = colMap[PRODUCT_BULK_COL.minStock] ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.minStock])) : '';
    const activeRaw = colMap[PRODUCT_BULK_COL.active] ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.active])) : '';
    const insumoRaw = colMap[PRODUCT_BULK_COL.isInsumo] ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.isInsumo])) : '';
    const vendibleRaw = colMap[PRODUCT_BULK_COL.isVendible]
      ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.isVendible]))
      : '';
    const machineryRaw = colMap[PRODUCT_BULK_COL.machineryReading]
      ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.machineryReading]))
      : '';
    const warehousesRaw = colMap[PRODUCT_BULK_COL.warehouses]
      ? cellText(row.getCell(colMap[PRODUCT_BULK_COL.warehouses]))
      : '';

    if (!idRaw && !skuRaw && !nameRaw && !unitRaw) {
      previewRows.push({ excelRow: r, status: 'empty', cols: {}, issue: '' });
      continue;
    }

    const colsPreview = {
      id: idRaw,
      sku: skuRaw,
      nombre: nameRaw,
      unidad: unitRaw,
      stock: minRaw,
      activo: activeRaw,
      insumo: insumoRaw,
      vendible: vendibleRaw,
      lecturaMaquinaria: machineryRaw,
      almacenes: warehousesRaw,
    };

    const id = parseInt(String(idRaw).trim(), 10);
    if (!Number.isFinite(id) || id < 1) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: 'ID de producto inválido.',
      });
      continue;
    }

    const existing = productById.get(id);
    if (!existing) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `No existe producto con ID ${id}.`,
      });
      continue;
    }

    if (existing.linked_to_recipe) {
      const recipeVendibleEditable = Boolean(opts.produccionEnabled && opts.ventasEnabled);
      if (!recipeVendibleEditable) {
        previewRows.push({
          excelRow: r,
          status: 'invalid',
          cols: colsPreview,
          issue: 'Producto vinculado a receta elaborada; no se puede editar masivamente.',
        });
        continue;
      }

      // Solo vendible: cualquier otro cambio respecto al sistema invalida la fila.
      const otherChanges = [];
      if (nameRaw && nameRaw !== (existing.name || '')) otherChanges.push('nombre');
      if (unitRaw) {
        const unitParsedTmp = resolveUnitCode(unitRaw, unitByCode);
        if (unitParsedTmp.ok && !unitParsedTmp.skip && unitParsedTmp.code !== (existing.unit_of_measure || '')) {
          otherChanges.push('unidad');
        }
      }
      if (minRaw !== '') {
        const minParsedTmp = parseMinStock(minRaw);
        if (minParsedTmp.ok && !minParsedTmp.skip && minParsedTmp.value !== Number(existing.min_stock ?? 0)) {
          otherChanges.push('stock mín.');
        }
      }
      if (activeRaw !== '' && parseActiveCell(activeRaw) !== Boolean(existing.is_active)) {
        otherChanges.push('activo');
      }
      if (opts.insumoEnabled && insumoRaw !== '' && parseActiveCell(insumoRaw) !== Boolean(existing.is_insumo)) {
        otherChanges.push('insumo');
      }
      if (opts.machineryEnabled && machineryRaw !== ''
        && parseActiveCell(machineryRaw) !== Boolean(existing.machinery_register_enabled)) {
        otherChanges.push('lectura maq.');
      }
      if (opts.warehouseVisibilityEnabled && warehousesRaw !== '') {
        const wh = resolveWarehouseIds(warehousesRaw, warehouseByCode);
        if (wh.ok && !wh.skip) {
          const newIds = wh.clear ? [] : wh.ids;
          const oldIds = (existing.warehouse_ids || existing.warehouses?.map((w) => w.id) || [])
            .map(Number)
            .sort((a, b) => a - b);
          const sortedNew = [...newIds].map(Number).sort((a, b) => a - b);
          if (JSON.stringify(sortedNew) !== JSON.stringify(oldIds)) {
            otherChanges.push('almacenes');
          }
        }
      }

      if (otherChanges.length > 0) {
        previewRows.push({
          excelRow: r,
          status: 'invalid',
          cols: colsPreview,
          issue: `Producto de receta: solo se puede cambiar «vendible» (intentó: ${otherChanges.join(', ')}).`,
        });
        continue;
      }

      if (!opts.ventasEnabled || vendibleRaw === '') {
        previewRows.push({
          excelRow: r,
          status: 'unchanged',
          cols: {
            ...colsPreview,
            sku: existing.sku || skuRaw,
            nombre: existing.name || nameRaw,
          },
          issue: 'Sin cambios de vendible.',
        });
        continue;
      }

      const isVendible = parseActiveCell(vendibleRaw);
      if (isVendible === Boolean(existing.is_vendible)) {
        previewRows.push({
          excelRow: r,
          status: 'unchanged',
          cols: {
            ...colsPreview,
            sku: existing.sku || skuRaw,
            nombre: existing.name || nameRaw,
          },
          issue: 'Sin cambios respecto al sistema.',
        });
        continue;
      }

      const payload = { id, is_vendible: isVendible };
      const changes = ['vendible'];
      previewRows.push({
        excelRow: r,
        status: 'valid',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          cambios: changes.join(', '),
        },
        issue: '',
      });
      validTasks.push({ excelRow: r, payload, changes });
      continue;
    }

    const unitParsed = resolveUnitCode(unitRaw, unitByCode);
    if (!unitParsed.ok) {
      previewRows.push({ excelRow: r, status: 'invalid', cols: colsPreview, issue: unitParsed.error });
      continue;
    }

    const minParsed = parseMinStock(minRaw);
    if (!minParsed.ok) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Stock mínimo debe ser un número entero ≥ 0.',
      });
      continue;
    }

    let warehousesParsed = { ok: true, skip: true };
    if (opts.warehouseVisibilityEnabled && colMap[PRODUCT_BULK_COL.warehouses]) {
      warehousesParsed = resolveWarehouseIds(warehousesRaw, warehouseByCode);
      if (!warehousesParsed.ok) {
        previewRows.push({ excelRow: r, status: 'invalid', cols: colsPreview, issue: warehousesParsed.error });
        continue;
      }
    }

    const payload = { id };
    const changes = [];

    if (nameRaw && nameRaw !== (existing.name || '')) {
      payload.name = nameRaw.slice(0, 200);
      changes.push('nombre');
    }

    if (!unitParsed.skip && unitParsed.code !== (existing.unit_of_measure || '')) {
      payload.unit_of_measure = unitParsed.code;
      changes.push('unidad');
    }

    if (!minParsed.skip && minParsed.value !== Number(existing.min_stock ?? 0)) {
      payload.min_stock = minParsed.value;
      changes.push('stock mín.');
    }

    if (activeRaw !== '') {
      const isActive = parseActiveCell(activeRaw);
      if (isActive !== Boolean(existing.is_active)) {
        payload.is_active = isActive;
        changes.push('activo');
      }
    }

    if (opts.insumoEnabled && insumoRaw !== '') {
      const isInsumo = parseActiveCell(insumoRaw);
      if (isInsumo !== Boolean(existing.is_insumo)) {
        payload.is_insumo = isInsumo;
        changes.push('insumo');
      }
    }

    if (opts.ventasEnabled && vendibleRaw !== '') {
      const isVendible = parseActiveCell(vendibleRaw);
      if (isVendible !== Boolean(existing.is_vendible)) {
        payload.is_vendible = isVendible;
        changes.push('vendible');
      }
    }

    if (opts.machineryEnabled && machineryRaw !== '') {
      const machinery = parseActiveCell(machineryRaw);
      if (machinery !== Boolean(existing.machinery_register_enabled)) {
        payload.machinery_register_enabled = machinery;
        changes.push('lectura maq.');
      }
    }

    if (opts.warehouseVisibilityEnabled && !warehousesParsed.skip) {
      const newIds = warehousesParsed.clear ? [] : warehousesParsed.ids;
      const oldIds = (existing.warehouse_ids || existing.warehouses?.map((w) => w.id) || [])
        .map(Number)
        .sort((a, b) => a - b);
      const sortedNew = [...newIds].map(Number).sort((a, b) => a - b);
      if (JSON.stringify(sortedNew) !== JSON.stringify(oldIds)) {
        payload.warehouse_ids = sortedNew;
        changes.push('almacenes');
      }
    }

    if (Object.keys(payload).length <= 1) {
      previewRows.push({
        excelRow: r,
        status: 'unchanged',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          nombre: existing.name || nameRaw,
          unidad: existing.unit_of_measure || unitRaw,
        },
        issue: 'Sin cambios respecto al sistema.',
      });
      continue;
    }

    previewRows.push({
      excelRow: r,
      status: 'valid',
      cols: {
        ...colsPreview,
        sku: existing.sku || skuRaw,
        cambios: changes.join(', '),
      },
      issue: '',
    });

    validTasks.push({ excelRow: r, payload, changes });
  }

  return {
    success: true,
    previewRows,
    validTasks,
    validCount: validTasks.length,
    emptyCount: previewRows.filter((x) => x.status === 'empty').length,
    invalidCount: previewRows.filter((x) => x.status === 'invalid').length,
    unchangedCount: previewRows.filter((x) => x.status === 'unchanged').length,
    showInsumoColumn: opts.insumoEnabled || Boolean(colMap[PRODUCT_BULK_COL.isInsumo]),
    showVendibleColumn: opts.ventasEnabled || Boolean(colMap[PRODUCT_BULK_COL.isVendible]),
    showMachineryColumn: opts.machineryEnabled || Boolean(colMap[PRODUCT_BULK_COL.machineryReading]),
    showWarehousesColumn: opts.warehouseVisibilityEnabled || Boolean(colMap[PRODUCT_BULK_COL.warehouses]),
  };
}

export async function executeProductsBulkEdit(validTasks) {
  const items = validTasks.map(({ payload }) => payload);
  try {
    const res = await productService.bulkUpdateProducts(items);
    if (!res.success) {
      return {
        updated: 0,
        skipped: 0,
        errors: (res.errors || []).map((e) => ({
          row: e.row ?? e.id,
          message: e.message || res.message,
        })),
      };
    }
    return {
      updated: res.updated ?? items.length,
      skipped: 0,
      errors: (res.errors || []).map((e) => ({
        row: e.row ?? e.id,
        message: e.message,
      })),
    };
  } catch (e) {
    return {
      updated: 0,
      skipped: 0,
      errors: [{ row: '-', message: formatApiError(e) }],
    };
  }
}

export function summarizeBulkEditResult(result) {
  const parts = [];
  if (result.updated > 0) parts.push(`${result.updated} actualizado(s)`);
  if (result.skipped > 0) parts.push(`${result.skipped} omitido(s)`);
  if (result.errors?.length) parts.push(`${result.errors.length} error(es)`);
  if (!parts.length) return 'No se realizaron cambios.';
  let msg = parts.join(' · ');
  if (result.errors?.length) {
    const detail = result.errors
      .slice(0, 5)
      .map((e) => `Fila ${e.row}: ${e.message}`)
      .join(' | ');
    msg += `. ${detail}`;
  }
  return msg;
}
